"use client"

import type React from "react"
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LogOut,
  Menu,
  X,
  MessageSquare,
  FileText,
  FolderOpen,
  Scale,
  BookOpen,
  CreditCard,
  ClipboardList,
  Settings,
  Moon,
  Sun,
  ChevronLeft,
  Home,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import FloatingChat from '../components/FloatingChat';
import NotificationBell from '../../features/scan-panel/components/NotificationBell';
import ResourceMonitor from '../components/ResourceMonitor';

interface LayoutProps {
  children: React.ReactNode;
}

// Same order as the route table (routes/index.tsx). Every member sees every
// entry: what each role may do on a page is decided by the page and the server.
const NAVIGATION = [
  { to: '/', labelKey: 'nav.dashboard', icon: Home },
  { to: '/chat', labelKey: 'nav.chat', icon: MessageSquare },
  { to: '/documents', labelKey: 'nav.documents', icon: FolderOpen },
  { to: '/bank-statements', labelKey: 'nav.bankStatements', icon: CreditCard },
  { to: '/legal-acts', labelKey: 'nav.legalActs', icon: FileText },
  { to: '/case-law', labelKey: 'nav.caseLaw', icon: Scale },
  { to: '/doctrine', labelKey: 'nav.doctrine', icon: BookOpen },
  { to: '/policies', labelKey: 'nav.policies', icon: ClipboardList },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings },
];

export default function ModernLayout({ children }: LayoutProps) {
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });
  const [isMobile, setIsMobile] = useState(false);

  const location = useLocation();
  const pathname = location.pathname;
  const { logout, user } = useAuth();

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setIsSidebarOpen(false);
        setIsSidebarCollapsed(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Dark mode toggle
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const activeLink = NAVIGATION.find(link => link.to === pathname);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      {/* Mobile menu button */}
      {isMobile && (
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="fixed top-3 left-4 z-50 p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800 transition-all duration-200"
        >
          {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      )}

      {/* Top Bar */}
      <header className="fixed top-0 right-0 left-0 lg:left-64 z-[60] h-14 bg-white border-b border-gray-200 dark:bg-gray-900 dark:border-gray-800 transition-all duration-300"
        style={{ left: isMobile ? 0 : (isSidebarCollapsed ? '5rem' : '16rem') }}
      >
        <div className="h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isMobile && (
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <ChevronLeft className={`h-5 w-5 transition-transform duration-200 ${isSidebarCollapsed ? 'rotate-180' : ''}`} />
              </button>
            )}
            <div className="flex flex-col">
              <h2 className="font-display text-base font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {t(activeLink?.labelKey ?? 'nav.dashboard')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('app.tagline')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* System resource monitor */}
            <ResourceMonitor />

            {/* Notification bell */}
            <NotificationBell />

            {/* Dark mode toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isDarkMode ? 'dark' : 'light'}
                  initial={{ rotate: -180, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 180, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isDarkMode ? (
                    <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  )}
                </motion.div>
              </AnimatePresence>
            </button>

            {/* User avatar */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-700 text-white">
              <div className="h-7 w-7 rounded-md bg-white/20 flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.email?.[0]?.toUpperCase() || t('dashboard.defaultUser').charAt(0)}
                </span>
              </div>
              <span className="text-white text-sm font-medium hidden sm:block">
                {user?.email?.split('@')[0] || ''}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <AnimatePresence>
        {(isSidebarOpen || !isMobile) && (
          <>
            {/* Mobile overlay */}
            {isMobile && isSidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                onClick={() => setIsSidebarOpen(false)}
              />
            )}

            {/* Sidebar content */}
            <motion.aside
              initial={isMobile ? { x: -256 } : false}
              animate={{ x: 0 }}
              exit={isMobile ? { x: -256 } : undefined}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`fixed top-0 left-0 z-40 h-full bg-white border-r border-gray-200 dark:bg-gray-900 dark:border-gray-800 flex flex-col transition-all duration-300 ${
                isSidebarCollapsed && !isMobile ? 'w-20' : 'w-64'
              }`}
            >
              {/* Logo/Title */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 flex items-center justify-center shrink-0">
                    <img src="/logo.svg" alt={t('layout.logoAlt')} className="h-9 w-auto dark:invert" />
                  </div>
                  {!isSidebarCollapsed && (
                    <div>
                      <h1 className="font-display font-bold tracking-tight text-xl text-gray-900 dark:text-gray-100">{t('app.name')}</h1>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('app.tagline')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Links */}
              <nav className="flex-1 overflow-y-auto py-4 px-3 custom-scrollbar">
                <div className="space-y-1">
                  {NAVIGATION.map((link) => {
                    const Icon = link.icon;
                    const isActive = pathname === link.to;
                    const label = t(link.labelKey);

                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => isMobile && setIsSidebarOpen(false)}
                        className="group relative"
                        title={isSidebarCollapsed ? label : undefined}
                      >
                        <div className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                          ${isActive
                            ? 'bg-primary-50 text-primary-700 border-l-2 border-primary-600 dark:bg-primary-900/30 dark:text-primary-300'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                          }
                        `}>
                          <Icon className={`h-5 w-5 ${isActive ? 'text-primary-600' : ''}`} />
                          {!isSidebarCollapsed && (
                            <span className="font-medium text-sm">{label}</span>
                          )}
                        </div>

                        {/* Tooltip for collapsed sidebar */}
                        {isSidebarCollapsed && !isMobile && (
                          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
                            {label}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </nav>

              {/* Footer info (the mark is already at the top: here only the name) */}
              {!isSidebarCollapsed && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-center mb-3">
                      <img src="/nd.svg" alt={t('layout.nativeDigitalAlt')} className="h-2 w-auto opacity-60 dark:invert" />
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      <div>{t('layout.copyright')}</div>
                      <a
                        href={t('layout.websiteUrl')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-gray-700 dark:hover:text-gray-200 hover:underline underline-offset-2"
                      >
                        {t('layout.website')}
                      </a>
                    </div>
                </div>
              )}

              {/* Logout Button */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-800">
                <button
                  onClick={logout}
                  className={`w-full flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-center gap-2'} px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group`}
                  title={isSidebarCollapsed ? t('nav.logout') : undefined}
                  aria-label={t('nav.logout')}
                >
                  <LogOut className="h-5 w-5" />
                  {!isSidebarCollapsed && <span className="font-medium text-sm">{t('nav.logout')}</span>}
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <main
        className="transition-all duration-300 pt-14"
        style={{ marginLeft: isMobile ? 0 : (isSidebarCollapsed ? '5rem' : '16rem') }}
      >
        <div className="p-8">
          <motion.div
            key={pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        </div>
      </main>
      <FloatingChat />
    </div>
  );
}
